import React, { createElement } from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import { Home, Users, Wrench } from '../../lib/icons';
// @ts-ignore — image module resolved by bundler
import proactiveMaintenanceBg from '../../assets/hd_gutter_cleaning.jpg';

const FEATURES = [
  {
    icon: <Home size={28} color="#ffffff" />,
    title: 'Digital Record Keeping',
    text: 'Track all your home expenses, maintenance history, and important documents in one secure place',
    imageUrl: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&q=80',
  },
  {
    icon: <Users size={28} color="#ffffff" />,
    title: 'Service Provider Management',
    text: 'Easily manage and coordinate with all your home service providers',
    imageUrl: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=800&q=80',
  },
  {
    icon: <Wrench size={28} color="#ffffff" />,
    title: 'Proactive Maintenance',
    text: 'Stay ahead of home maintenance with personalized schedules and reminders',
    imageUrl: proactiveMaintenanceBg,
  },
];

const FeatureCard = ({
  icon,
  title,
  text,
  imageUrl,
}: {
  icon: React.ReactNode;
  title: string;
  text: string;
  imageUrl: string;
}) => {
  if (Platform.OS === 'web') {
    return createElement(
      'div',
      {
        style: {
          position: 'relative',
          flex: 1,
          minWidth: 200,
          minHeight: 240,
          borderRadius: 16,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
        },
      },
      // Background photo
      createElement('div', {
        'aria-hidden': true,
        style: {
          position: 'absolute',
          inset: 0,
          backgroundImage: `url(${imageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          transition: 'transform 0.4s ease',
        },
      }),
      // Gradient overlay
      createElement('div', {
        'aria-hidden': true,
        style: {
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(to top, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.25) 55%, rgba(0,0,0,0.05) 100%)',
        },
      }),
      // Content
      createElement(
        'div',
        {
          style: {
            position: 'relative',
            zIndex: 1,
            padding: 24,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          },
        },
        createElement('div', { style: { marginBottom: 4 } }, icon),
        createElement('div', { style: { fontSize: 16, fontWeight: '600', color: '#ffffff', lineHeight: '1.3' } }, title),
        createElement('div', { style: { fontSize: 14, color: 'rgba(255,255,255,0.8)', lineHeight: '1.5' } }, text),
      ),
    );
  }

  // Native fallback — plain card without background image
  return (
    <View style={styles.featureCardNative}>
      <Home size={28} color="#111827" />
      <Text style={styles.featureTitleNative}>{title}</Text>
      <Text style={styles.featureTextNative}>{text}</Text>
    </View>
  );
};

export const WelcomeStep = () => {
  return (
    <View style={styles.stepContainer}>
      <View style={styles.welcomeSection}>
        <Text style={styles.welcomeTitle}>Welcome to HoneyDone</Text>
        <Text style={styles.welcomeSubtitle}>
          Your complete home management solution. Keep track of everything your home needs, from regular maintenance to service providers.
        </Text>
      </View>
      <View style={styles.featuresGrid}>
        {FEATURES.map((f) => (
          <FeatureCard key={f.title} {...f} />
        ))}
      </View>
      <View style={styles.whySection}>
        <Text style={styles.whyTitle}>Why Choose HoneyDone?</Text>
        <Text style={styles.whyText}>
          We help you protect your biggest investment by making home maintenance simple and organized. 
          Whether you manage everything yourself or work with service providers, HoneyDone keeps you 
          on top of your home's needs.
        </Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  stepContainer: {
    gap: 32,
  },
  welcomeSection: {
    gap: 12,
    alignItems: 'center',
  },
  welcomeTitle: {
    fontSize: 30,
    fontWeight: 'bold',
    color: '#111827',
  },
  welcomeSubtitle: {
    fontSize: 18,
    color: '#4b5563',
    textAlign: 'center',
    maxWidth: 672,
  },
  featuresGrid: {
    ...(Platform.OS === 'web' && {
      flexDirection: 'row',
      flexWrap: 'wrap',
    }),
    gap: 16,
  },
  featureCardNative: {
    padding: 24,
    backgroundColor: '#f9fafb',
    borderRadius: 12,
    gap: 12,
  },
  featureTitleNative: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
  },
  featureTextNative: {
    fontSize: 14,
    color: '#4b5563',
  },
  whySection: {
    backgroundColor: '#f9fafb',
    borderRadius: 12,
    padding: 24,
    maxWidth: 672,
    alignSelf: 'center',
  },
  whyTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 8,
  },
  whyText: {
    fontSize: 14,
    color: '#4b5563',
  },
});
